import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, TextField, Button, Box, IconButton, CircularProgress, Snackbar
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import SendIcon from '@material-ui/icons/Send';
import { EvaluationCategoryData } from '../../../services';

const ratingLabel = (val) => {
  const labels = { 1: 'Significantly Below Expectations', 2: 'Needs Improvement', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Significantly Exceeds Expectations' };
  return labels[val] || '';
};

const ratingStars = (val) => '★'.repeat(val || 0) + '☆'.repeat(5 - (val || 0));

const CATEGORY_WEIGHTS = {
  'court presence': 30,
  'mechanics': 20,
  'play calling': 20,
  'communication': 15,
  'fitness': 15,
};

const getCategoryWeight = (name) =>
  CATEGORY_WEIGHTS[(name || '').trim().toLowerCase()] || 0;

const getCategoryScore = (cat, form) => {
  const activeSubs = (cat.subCategories || []).filter((s) => s.subCategoryActive);
  if (activeSubs.length > 0) {
    const ratings = activeSubs.map((s) => form[`sub_${s.id}`] || 0);
    const valid = ratings.filter((r) => r > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
  }
  return form[`cat_${cat.id}`] || 0;
};

const formatEvaluationEmail = (form, evaluationCategories, tangibles, selectedTangibles, competencies, selectedCompetencies, selectedEvent) => {
  const date = form.evaluationDate || new Date().toISOString().split('T')[0];
  const lines = [];

  lines.push('BASKETBALL OFFICIATING EVALUATION REPORT');
  lines.push('═'.repeat(55));
  lines.push('');

  // ── Basic Info (matches form header) ──
  lines.push(`Ref Evaluation:   ${form.refereeName || 'N/A'}`);
  if (selectedEvent?.eventName) {
    lines.push(`Event:            ${selectedEvent.eventName}`);
  }
  lines.push(`Review Date:      ${date}`);
  const locationParts = [
    selectedEvent?.stateName || selectedEvent?.stateOther || '',
    selectedEvent?.city || '',
  ].filter(Boolean);
  if (locationParts.length > 0) {

   
    let str=[]
    if(selectedEvent?.city){
      str.push(`${selectedEvent.city}`)
    }
    if(selectedEvent?.stateName || selectedEvent?.stateOther){
      str.push(` ${selectedEvent.stateName || selectedEvent.stateOther}`)
    }
    if(selectedEvent?.countryName){
      str.push(` ${selectedEvent.countryName}`)
    }
  
    lines.push(`Address:         ${str.join(', ')}`);
  }
  if (selectedEvent?.gameTypeName) {
    lines.push(`Game Type:        ${selectedEvent.gameTypeName}`);
  }
  lines.push('');

  // ── Category Sections ──
  const activeCategories = (evaluationCategories || []).filter(cat => cat.categoryActive);

  const categoryData = activeCategories.map(cat => {
    const score = getCategoryScore(cat, form);
    const weight = getCategoryWeight(cat.categoryName);
    const weightedContribution = score * (weight / 100);
    return { ...cat, score, weight, weightedContribution };
  });

  categoryData.forEach(cat => {
    lines.push('─'.repeat(55));
    lines.push(`${cat.categoryName.toUpperCase()} (${cat.weight}%)    Calculated Score: ${cat.score.toFixed(2)}/5`);
    lines.push(`${cat.score.toFixed(2)} × ${cat.weight}% = ${cat.weightedContribution.toFixed(2)}`);
    lines.push('');

    const activeSubCategories = (cat.subCategories || []).filter(sub => sub.subCategoryActive);

    if (activeSubCategories.length > 0) {
      activeSubCategories.forEach(sub => {
        const rating = form[`sub_${sub.id}`] || 0;
        const padding = ' '.repeat(Math.max(1, 28 - sub.subCategoryName.length));
        lines.push(`  ${sub.subCategoryName}${padding}${ratingStars(rating)}  ${rating}/5  ${ratingLabel(rating)}`);
        const subKey = `sub_comp_${sub.id}`;
        const subCompNames = (selectedCompetencies[subKey] || [])
          .map(cId => (competencies || []).find(c => c.id === cId))
          .filter(Boolean)
          .map(c => c.competencyName);
        if (subCompNames.length > 0) {
          lines.push(`    Competencies: ${subCompNames.join(', ')}`);
        }
      });
    } else {
      const rating = form[`cat_${cat.id}`] || 0;
      const padding = ' '.repeat(Math.max(1, 28 - cat.categoryName.length));
      lines.push(`  ${cat.categoryName}${padding}${ratingStars(rating)}  ${rating}/5  ${ratingLabel(rating)}`);
      const catKey = `cat_comp_${cat.id}`;
      const catCompNames = (selectedCompetencies[catKey] || [])
        .map(cId => (competencies || []).find(c => c.id === cId))
        .filter(Boolean)
        .map(c => c.competencyName);
      if (catCompNames.length > 0) {
        lines.push(`    Competencies: ${catCompNames.join(', ')}`);
      }
    }

    const comment = form[`category_${cat.id}`] || '';
    if (comment.trim()) {
      lines.push('');
      lines.push(`  Comment: ${comment.trim()}`);
    }
    lines.push('');
  });

  // ── Score Breakdown Table ──
  lines.push('═'.repeat(55));
  lines.push('SCORE BREAKDOWN');
  lines.push('─'.repeat(55));
  const colHeader = 'Category                   Avg Score  Weight  Contribution';
  lines.push(colHeader);
  lines.push('─'.repeat(55));
  categoryData.forEach(cat => {
    const name = cat.categoryName.padEnd(27);
    const avg = cat.score.toFixed(2).padStart(5).padEnd(10);
    const wt = (`${cat.weight}%`).padStart(4).padEnd(8);
    const contrib = cat.weightedContribution.toFixed(2).padStart(6);
    lines.push(`${name}${avg}${wt}${contrib}`);
  });
  lines.push('─'.repeat(55));
  const totalWeight = categoryData.reduce((sum, c) => sum + c.weight, 0);
  const overallScore = totalWeight > 0
    ? categoryData.reduce((sum, c) => sum + c.weightedContribution, 0)
    : 0;
  lines.push(`${'Overall Weighted Score'.padEnd(27)}${' '.repeat(10)}${' '.repeat(8)}${overallScore.toFixed(2).padStart(6)}`);
  lines.push('═'.repeat(55));

  // ── Evaluation Summary ──
  if (form.evaluationSummary) {
    lines.push('');
    lines.push('EVALUATION SUMMARY');
    lines.push('─'.repeat(30));
    lines.push(form.evaluationSummary);
  }

    // ── Intangibles ──
  const selectedTangibleNames = (tangibles || [])
    .filter(t => (selectedTangibles || []).includes(t.id))
    .map(t => t.name);
  if (selectedTangibleNames.length > 0) {
    lines.push('');
    lines.push('INTANGIBLES');
    lines.push('─'.repeat(30));
    lines.push(selectedTangibleNames.join(', '));
  }

  lines.push('');
  lines.push('─'.repeat(55));
  //lines.push('Generated by NBA Officiating Opportunities');
  lines.push(`Report Date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`);

  return lines.join('\n');
};

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: '#1d428a',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 3),
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  closeBtn: {
    color: '#ffffff',
  },
  fieldLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
  },
  subjectBox: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#f6f6f6',
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  bodyPreview: {
    maxHeight: 300,
    overflowY: 'auto',
    backgroundColor: '#f6f6f6',
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
  },
  preText: {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    lineHeight: 1.6,
    margin: 0,
  },
  bodyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
}));

const EmailModal = ({ form, evaluationCategories, tangibles, selectedTangibles, competencies, selectedCompetencies, selectedEvent, userEmail, onClose }) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState({ message: '', type: '' });
  const [showToast, setShowToast] = useState(false);
  const contentRef = useRef(null);
  const emailBody = formatEvaluationEmail(form, evaluationCategories, tangibles, selectedTangibles, competencies, selectedCompetencies, selectedEvent);
  const subject = `Prospect Evaluation — ${form.refereeName || 'N/A'} — ${form.evaluationDate || 'N/A'}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(emailBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = emailBody;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendEmail = async () => {
    setIsSending(true);
    setSendStatus({ message: '', type: '' });
    try {
      await EvaluationCategoryData.sendEvaluationEmail({
        toEmails: userEmail || '',
        subject,
        body: emailBody,
      });
      setShowToast(true);
    } catch (error) {
      setSendStatus({
        message: `Failed to send email: ${error?.message || 'Unknown error'}`,
        type: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setShowToast(false);
    onClose();
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose} disableBackdropClick>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <MailOutlineIcon />
          <div>
            <Typography variant="h6" style={{ color: '#ffffff' }}>Email Evaluation</Typography>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Preview and send the formatted evaluation report
            </Typography>
          </div>
        </Box>
        <IconButton size="small" onClick={onClose} className={classes.closeBtn}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <Typography className={classes.fieldLabel}>Send To</Typography>
          <Box className={classes.subjectBox}>{userEmail || 'Your email'}</Box>
        </Box>

        <Box mb={2}>
          <Typography className={classes.fieldLabel}>Subject</Typography>
          <Box className={classes.subjectBox}>{subject}</Box>
        </Box>

        <Box>
          <Box className={classes.bodyHeader}>
            <Typography className={classes.fieldLabel}>Email Body</Typography>
            <Button
              size="small"
              variant="outlined"
              color={copied ? 'default' : 'primary'}
              onClick={handleCopy}
              startIcon={copied ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
              style={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </Button>
          </Box>
          <Box ref={contentRef} className={classes.bodyPreview}>
            <pre className={classes.preText}>{emailBody}</pre>
          </Box>
        </Box>

        {sendStatus.message && sendStatus.type === 'error' && (
          <Box mt={2}>
            <Typography
              variant="body2"
              style={{
                color: '#d32f2f',
                fontWeight: 500,
              }}
            >
              {sendStatus.message}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Close
        </Button>
        <Button
          onClick={handleSendEmail}
          disabled={isSending}
          color="primary"
          variant="contained"
          startIcon={isSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
        >
          {isSending ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>

      <Snackbar
        open={showToast}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleToastClose}
          severity="success"
          elevation={6}
          variant="filled"
        >
          Email sent successfully!
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default EmailModal;
